import { Pie } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { BasePieChartProps, CustomPieChart } from "../types";

import "../styles.css";

const BasePieChart = ({
  data,
  height,
  title,
  className,
}: BasePieChartProps) => {
  // Ensure minimum angle for visibility (in degrees)
  const minAngle = 5; // 5 degrees minimum

  // Process data to ensure minimum angles
  const processedData = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      minAngle: minAngle,
      borderWidth: 2,
    })),
  };
  const { t } = useTranslation();

  const centerTextPlugin = {
    id: "centerText",
    afterDatasetsDraw: (chart: CustomPieChart) => {
      const ctx = chart.ctx;
      const centerX = chart.width / 2;
      const centerY = chart.height / 2;
      const dataset = chart.data.datasets[0];
      if (dataset) {
        const total = dataset.totalCount;

        ctx.save();

        const chartSize = Math.min(chart.width, chart.height);
        const responsiveFontSize = Math.max(24, Math.min(40, chartSize * 0.08));
        const responsiveLabelSize = Math.max(
          14,
          Math.min(20, chartSize * 0.04),
        );

        ctx.font = `bold ${responsiveFontSize}px Roboto`;
        ctx.fillStyle = "#1e1e1e";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(total.toString(), centerX, centerY - 2);

        ctx.font = `${responsiveLabelSize}px Roboto`;
        ctx.fillStyle = "#1e1e1e";
        ctx.fillText(t("total"), centerX, centerY + responsiveFontSize * 0.6);

        ctx.restore();
      }
    },
  };

  const leaderLinesPlugin = {
    id: "leaderLines",
    afterDatasetsDraw: (chart: CustomPieChart) => {
      const ctx = chart.ctx;
      const meta = chart.getDatasetMeta(0);

      if (!meta.data || meta.data.length === 0) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      meta.data.forEach((arc: any, index: number) => {
        const dataset = chart.data.datasets[0];
        if (!dataset) return;

        const label = chart.data.labels?.[index];
        const value = dataset.data[index];

        const centerX = arc.x;
        const centerY = arc.y;
        const radius = arc.outerRadius;
        const angle = arc.startAngle + (arc.endAngle - arc.startAngle) / 2;

        // Calculate line start point (edge of pie slice)
        const borderWidth = dataset.borderWidth || 0;
        const adjustedRadius = radius - borderWidth / 2;
        const lineStartX = centerX + Math.cos(angle) * adjustedRadius;
        const lineStartY = centerY + Math.sin(angle) * adjustedRadius;

        // Calculate line middle point
        const lineLength = 20;
        const lineMidX =
          centerX + Math.cos(angle) * (adjustedRadius + lineLength);
        const lineMidY =
          centerY + Math.sin(angle) * (adjustedRadius + lineLength);

        // Calculate horizontal line extension
        const extensionLength = 82;
        const normalizedAngle =
          ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const isRightSide =
          normalizedAngle < Math.PI / 2 || normalizedAngle > (3 * Math.PI) / 2;
        const lineEndX =
          lineMidX + (isRightSide ? extensionLength : -extensionLength);

        // Draw the leader line
        ctx.save();
        const strokeColors = dataset.strokeColors;
        const strokeColor = strokeColors?.[index] || "#999999";
        ctx.strokeStyle = strokeColor;

        ctx.beginPath();
        // Line from pie edge to bend point
        ctx.moveTo(lineStartX, lineStartY);
        ctx.lineTo(lineMidX, lineMidY);

        // Horizontal line extension
        ctx.lineTo(lineEndX, lineMidY);

        ctx.stroke();

        const chartSize = Math.min(chart.width, chart.height);
        const responsiveLabelFontSize = Math.max(
          14,
          Math.min(20, chartSize * 0.04),
        );

        ctx.font = `${responsiveLabelFontSize}px Roboto`;
        ctx.fillStyle = strokeColor;
        ctx.textAlign = isRightSide ? "right" : "left";

        // Handle multi-line labels for long text or multiple words
        const labelY = lineMidY - 8;
        ctx.fillStyle = "rgba(0, 0, 0, 0.7)";

        const labelText = String(label || "");
        const words = labelText.split(" ");
        const shouldSplit = words.length > 1 || labelText.length > 12;

        if (shouldSplit && words.length > 1) {
          // Split into two lines for multiple words
          const firstLine = words[0];
          const secondLine = words.slice(1).join(" ");

          ctx.fillText(firstLine || "", lineEndX, labelY - 12);
          ctx.fillText(secondLine || "", lineEndX, labelY + 4);

          // Position number below the second line
          const numberY = lineMidY + 24;
          ctx.fillStyle = strokeColor;
          ctx.fillText(String(value || ""), lineEndX, numberY);
        } else if (shouldSplit && labelText.length > 12) {
          // Split long single words at a reasonable point
          const midPoint = Math.ceil(labelText.length / 2);
          const firstLine = labelText.substring(0, midPoint);
          const secondLine = labelText.substring(midPoint);

          ctx.fillText(firstLine, lineEndX, labelY - 6);
          ctx.fillText(secondLine, lineEndX, labelY + 8);

          // Position number below the second line
          const numberY = lineMidY + 24;
          ctx.fillStyle = strokeColor;
          ctx.fillText(String(value || ""), lineEndX, numberY);
        } else {
          // Single line labels
          ctx.fillText(labelText, lineEndX, labelY);

          // Position number below the line
          const numberY = lineMidY + 16;
          ctx.fillStyle = strokeColor;
          ctx.fillText(String(value || ""), lineEndX, numberY);
        }

        ctx.restore();
      });
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 50,
        bottom: 50,
        left: 100,
        right: 100,
      },
    },
    plugins: {
      legend: {
        display: false,
        labels: {
          font: {
            family: "Roboto",
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: false,
      },
    },
    elements: {
      arc: {
        borderWidth: 2,
        hoverBorderWidth: 0,
        hoverOffset: 0,
      },
    },
    cutout: "50%",
    circumference: 360,
    rotation: 0,
  };

  return (
    <div
      style={{ height: `${height}px` }}
      className={"base-pie-chart " + (className ? className : "")}
    >
      {title && <div className="body-m-medium">{title}</div>}

      <Pie
        data={processedData}
        options={options}
        plugins={[centerTextPlugin, leaderLinesPlugin]}
      />
    </div>
  );
};

export default BasePieChart;
